/**
 * Sponsor profile program hook - Elm-ish state management.
 * 
 * This hook follows the Elm architecture pattern:
 * - Model: Immutable state
 * - Update: Pure function to transition state
 * - Effects: Side effects handled separately
 * 
 * Security: All API calls are made here, not in the view.
 * 
 * Profile activation per REQ-125, REQ-126, REQ-127:
 * - Gender-first flow: goal disabled until gender selected
 * - Age validation: 18-120
 * - Missing activation fields from API
 */
import { useReducer, useCallback, useEffect } from 'react';
import { updateSponsorProfile } from './update';
import { runEffect } from './effects';
import type { SponsorProfileModel } from './model';
import type { SponsorProfileMsg } from './messages';
import type { ProfileFormData } from './model';
import { initialModel } from './model';
import type { Gender, SponsorGoal } from '@/api/profiles';

/**
 * Effect types that can be triggered.
 */
export type SponsorProfileEffect =
  | { type: 'LoadProfile' }
  | { type: 'SaveProfile'; form: ProfileFormData };

/**
 * Result of using the sponsor profile program.
 * Provides model, methods, and effect runner.
 */
export interface UseSponsorProfileProgramResult {
  /** Current model state */
  model: SponsorProfileModel;
  
  /** Start loading the profile */
  start: () => void;
  
  /** Start editing mode */
  startEdit: () => void;
  
  /** Update form field */
  updateForm: (form: ProfileFormData) => void;
  
  /** Save the profile */
  save: (form: ProfileFormData) => void;
  
  /** Cancel editing */
  cancel: () => void;
  
  /** Retry after error */
  retry: () => void;
  
  /** Change gender (per REQ-126 - must be chosen first) */
  setGender: (gender: Gender | undefined) => void;
  
  /** Change goal (per REQ-127 - only available after gender) */
  setGoal: (goal: SponsorGoal | undefined) => void;
  
  /** Change age (per REQ-125 - must be 18-120) */
  setAge: (age: number | undefined) => void;
}

// Reducer action types for useReducer
type ReducerAction = SponsorProfileMsg;

/**
 * Hook for sponsor profile state management.
 * Returns model and dispatch-like methods.
 */
export function useSponsorProfileProgram(): UseSponsorProfileProgramResult {
  const [model, dispatch] = useReducer(
    (state: SponsorProfileModel, action: ReducerAction) => updateSponsorProfile(state, action),
    initialModel()
  );

  // Effect runner - handles async side effects
  const runEffects = useCallback(async (effect: SponsorProfileEffect) => {
    await runEffect(effect, { dispatch });
  }, []);

  // Start loading on mount
  useEffect(() => {
    if (model.status === 'idle') {
      runEffects({ type: 'LoadProfile' });
    }
  }, [model.status, runEffects]);

  // Start loading
  const start = useCallback(() => {
    dispatch({ type: 'Retried' });
    runEffects({ type: 'LoadProfile' });
  }, [runEffects]);

  // Start editing
  const startEdit = useCallback(() => {
    dispatch({ type: 'EditStarted' });
  }, []);

  // Update form
  const updateForm = useCallback((form: ProfileFormData) => {
    dispatch({ type: 'FormUpdated', form });
  }, []);

  // Save profile
  const save = useCallback((form: ProfileFormData) => {
    runEffects({ type: 'SaveProfile', form });
  }, [runEffects]);

  // Cancel editing
  const cancel = useCallback(() => {
    dispatch({ type: 'EditCancelled' });
  }, []);

  // Retry after error
  const retry = useCallback(() => {
    dispatch({ type: 'Retried' });
    runEffects({ type: 'LoadProfile' });
  }, [runEffects]);

  // Change gender per REQ-126
  const setGender = useCallback((gender: Gender | undefined) => {
    dispatch({ type: 'GenderChanged', gender });
  }, []);

  // Change goal per REQ-127 (only available after gender)
  const setGoal = useCallback((goal: SponsorGoal | undefined) => {
    dispatch({ type: 'GoalChanged', goal });
  }, []);

  // Change age per REQ-125 (must be 18-120)
  const setAge = useCallback((age: number | undefined) => {
    dispatch({ type: 'AgeChanged', age });
  }, []);

  return {
    model,
    start,
    startEdit,
    updateForm,
    save,
    cancel,
    retry,
    setGender,
    setGoal,
    setAge,
  };
}
